import { lazy, Suspense, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useProfileQuery, useUpdateProfileMutation } from '@entities/Profile';
// Не из индексов фич: они тянут экраны игры. Здесь — данные хаба, старт миссии, окно уровня и рисунок жителя.
import { ART } from '@features/London/assets/art';
import { storiesKeys, useStartMissionMutation, useStoriesHubQuery } from '@features/Stories/model/queries';
import { LevelPrompt } from '@features/Stories/ui/LevelPrompt';
import { storyError } from '@features/Stories/ui/parts';
import { trackEvent } from '@shared/analytics';
import { buildStoryMissionPath, buildStoryPlayPath } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, RewardBadge, TokenIcon, toast } from '@shared/ui';

const MissionPortrait = lazy(() => import('./MissionPortrait'));

/** Лицо жителя миссии из его бюста (верх спокойного кадра); рисунка ещё нет — пиксельный портрет старой игры. */
function ResidentFace({ id, character }: { id: string | undefined; character: string }) {
  const sheet = id ? ART[id + '-bust'] : undefined;
  if (sheet?.cell) {
    const w = 48 * 1.6;
    const h = (w * sheet.cell[1]) / sheet.cell[0];
    return (
      <span
        className="block h-12 w-12 bg-no-repeat"
        style={{
          backgroundImage: `url(${sheet.url})`,
          backgroundSize: `${(sheet.cols ?? 3) * w}px ${(sheet.rows ?? 2) * h}px`,
          backgroundPosition: `${-(w - 48) / 2}px ${-h * 0.05}px`,
        }}
      />
    );
  }
  return (
    <Suspense fallback={null}>
      <MissionPortrait character={character} className="h-12 w-12" />
    </Suspense>
  );
}

/**
 * «Миссия дня» — строка «Списка дня» на главной: лицо жителя, что дают жетонами, название и кнопка. С 2026-11-03 —
 * миссия v2 (разговор с жителем, как в новой игре: docs/product/daily-boss-v2.md): начать — сразу на экран миссии,
 * начатую — продолжить, пройденную — полученные жетоны или итог. Уровень английского не выбран — сначала спросим.
 */
export function MissionRow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const hub = useStoriesHubQuery().data;
  const start = useStartMissionMutation();
  const profile = useProfileQuery();
  const update = useUpdateProfileMutation();
  const [ask, setAsk] = useState(false);
  if (!hub) return null;

  const daily = hub.daily;
  const runId = daily.run_id;
  const active = daily.status === 'active' && !!runId;
  const finished = daily.status === 'finished' || daily.status === 'abandoned';
  const items = daily.items ? Object.keys(daily.items) : daily.steps.map((s) => s.id);
  const stepsDone = daily.steps.filter((s) => s.done && items.includes(s.id)).length;
  // Начатая до выката v2 миссия — на прежнем экране.
  const runPath = (id: string) => (daily.v === 2 && (!active || daily.active_v2) ? buildStoryMissionPath(id) : buildStoryPlayPath(id));

  const play = (levelOk = false) => {
    trackEvent('home_mission_clicked', { action: active ? 'resume' : 'start' });
    if (active) {
      navigate(runPath(runId));
      return;
    }
    if (!levelOk && hub.need_level && !profile.data?.learner_level) {
      setAsk(true);
      return;
    }
    start.mutate(
      {},
      {
        onSuccess: (m) => navigate(buildStoryMissionPath(m.run_id)),
        onError: (err) => {
          const info = storyError(err);
          toast({
            tone: 'error',
            message:
              info.code === 'STORY_DEMO_LIMIT' || info.code === 'STORY_DEMO_CLOSED'
                ? t('stories.play.errors.guestLimit')
                : (info.message ?? t('stories.play.errors.generic')),
          });
        },
      },
    );
  };

  // Метка у надзаголовка: что дадут, сколько пунктов выполнено или что жетонов не дали.
  const noReward = finished && !daily.granted && !daily.done;
  const pill: { text: string; tone: 'accent' | 'muted' } | null = finished
    ? noReward
      ? { text: t('dashboard.home.mission.noReward'), tone: 'muted' }
      : null
    : active
      ? { text: t('dashboard.home.dailies.steps', { n: stepsDone, total: items.length }), tone: 'accent' }
      : { text: t('dashboard.home.mission.upTo', { count: daily.reward.perfect }), tone: 'accent' };

  return (
    <div className="dl-row flex items-center gap-3">
      <span aria-hidden className="dl-art lx-px-sm grid h-12 w-12 flex-none place-items-center overflow-hidden bg-[#3f86d9]">
        <ResidentFace id={daily.npc?.id} character={daily.character} />
      </span>
      <div className="dl-text min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">
          {t('stories.daily.kicker')}
          {pill && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-pill px-1.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal',
                pill.tone === 'accent' ? 'bg-accent/15 text-accent' : 'bg-white/[0.08] text-muted',
              )}
            >
              <TokenIcon size={12} className={pill.tone === 'muted' ? 'opacity-50 grayscale' : undefined} />
              {pill.text}
            </span>
          )}
        </div>
        <h3 className="mt-0.5 line-clamp-2 break-words font-serif text-[17px] font-medium leading-[22px] text-fg">{daily.title}</h3>
        {daily.npc ? (
          <p className="mt-0.5 truncate text-[13px] leading-[18px] text-muted">
            {daily.npc.name_ru}, {daily.npc.role_ru}
          </p>
        ) : null}
      </div>
      <div className="dl-action flex flex-none items-center gap-2">
        {active ? (
          <Button variant="outline" size="sm" onClick={() => play()}>
            {t('dashboard.home.dailies.resume')}
          </Button>
        ) : finished ? (
          daily.granted ? (
            <RewardBadge size="sm" amount={daily.granted} caption={t('rewards.earned', { count: daily.granted })} />
          ) : (
            runId && (
              <Button variant="ghost" size="sm" onClick={() => navigate(runPath(runId))}>
                {t('dashboard.home.dailies.result')}
              </Button>
            )
          )
        ) : (
          <Button variant="outline" size="sm" onClick={() => play()} disabled={start.isPending} aria-busy={start.isPending || undefined}>
            {start.isPending ? t('dashboard.home.dailies.starting') : t('dashboard.home.dailies.start')}
          </Button>
        )}
      </div>
      <LevelPrompt
        open={ask}
        value={profile.data?.learner_level ?? null}
        busy={update.isPending}
        onPick={(next) =>
          update.mutate(
            { learner_level: next },
            {
              onSuccess: () => {
                setAsk(false);
                void qc.invalidateQueries({ queryKey: storiesKeys.hub });
                play(true);
              },
            },
          )
        }
        onClose={() => setAsk(false)}
      />
    </div>
  );
}
